import React from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';

import { FormattedMessage, defineMessages } from 'react-intl';
import * as constants from '../../containers/Authorization/constants';
import Icon from '../Icon';
import Button from '../Button';
import Input from '../Input';
import Loader from '../Loader';
import Dimmer from '../Dimmer';
import Segment from '../Segment';
import Form from '../Form';
import Label from '../Label';
import Message from '../Message';
import './styles.css';

const messages = defineMessages({
  updateUser: {
    id: 'user.update.caption',
    defaultMessage: 'Edit {name}',
  },
  firstName: {
    id: 'user.firstName.label',
    defaultMessage: 'First Name',
  },
  lastName: {
    id: 'user.lastName.label',
    defaultMessage: 'Last Name',
  },
  email: {
    id: 'user.email.label',
    defaultMessage: 'Email',
  },
  buttonSave: {
    id: 'common.button.save',
    defaultMessage: 'SAVE',
  },
  buttonCancel: {
    id: 'common.button.cancel',
    defaultMessage: 'CANCEL',
  },
  generalError: {
    id: 'user.update.error.general',
    defaultMessage: 'Unable to update user',
  },
  userExists: {
    id: 'user.update.error.userExists',
    defaultMessage: 'User with provided email already exists!',
  },
  role: {
    id: 'user.role.label',
    defaultMessage: 'Role',
  },
  administrator: {
    id: 'user.update.role.administrator',
    defaultMessage: 'Administrator (All access)',
  },
  recruiter: {
    id: 'user.update.role.recruiter',
    defaultMessage: 'Recruiter',
  },
  coordinator: {
    id: 'user.update.role.coordinator',
    defaultMessage: 'Coordinator',
  },
  director: {
    id: 'user.update.role.director',
    defaultMessage: 'Director',
  },
  publishing: {
    id: 'user.update.role.publishing',
    defaultMessage: 'Publishing (Community access + College Profile Management)',
  },
  community: {
    id: 'user.update.role.community',
    defaultMessage: 'Community (Community access only)',
  },
  reports: {
    id: 'user.update.role.reports',
    defaultMessage: 'Reports',
  },
});

const repvisitMessages = defineMessages({
  administrator: {
    id: 'user.update.role.administrator',
    defaultMessage: 'Administrator (All access)',
  },
  publishing: {
    id: 'repvisits.user.update.role.publishing',
    defaultMessage: 'Publishing (Community access + RepVisits + College Profile Management)',
  },
  community: {
    id: 'repvisits.user.update.role.community',
    defaultMessage: 'Community (Community + RepVisits access)',
  },
});

function validateForm(formData) {
  return {
    missingFirstName: !formData.firstName,
    missingLastName: !formData.lastName,
    missingEmail: !formData.email,
    missingRole: !formData.role,
  };
}

export const elementIds = {
  firstNameId: 'user-form-first-name',
  lastNameId: 'user-form-last-name',
  emailId: 'user-form-email',
  roleId: 'user-form-role',
};

const missingValueLabel = (
  <Label role="tooltip" tabIndex={-1} basic color="red" pointing>
    <FormattedMessage id="change-password.error.missingValue" defaultMessage="Please enter a value" />
  </Label>
);

class UpdateUserForm extends React.PureComponent {
  static propTypes = {
    user: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
    }).isRequired,
    requesting: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    updateError: PropTypes.shape({
      generalError: PropTypes.bool,
      userExists: PropTypes.bool,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      email: props.user.email,
      role: props.user.role,
      errors: {},
    };
  }

  getModifiedFormFields = (formData) => {
    const modifiedFormFields = {};
    if (formData.firstName) {
      if (!this.props.user.firstName || formData.firstName.trim() !== this.props.user.firstName.trim()) {
        modifiedFormFields.firstName = formData.firstName.trim();
      }
    }
    if (formData.lastName) {
      if (!this.props.user.lastName || formData.lastName.trim() !== this.props.user.lastName.trim()) {
        modifiedFormFields.lastName = formData.lastName.trim();
      }
    }
    if (formData.email) {
      if (!this.props.user.email || formData.email.trim() !== this.props.user.email.trim()) {
        modifiedFormFields.email = formData.email.trim();
      }
    }
    if (formData.role && formData.role !== this.props.user.role) {
      modifiedFormFields.role = formData.role;
    }
    return modifiedFormFields;
  };

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    const formData = omit(this.state, 'errors');
    e.preventDefault();
    const errors = validateForm(formData);
    this.setState({ errors });
    if (!Object.keys(errors).some((key) => errors[key])) {
      const modifiedFormFields = this.getModifiedFormFields(formData);
      if (Object.getOwnPropertyNames(modifiedFormFields).length === 0) {
        // no changes
        this._onCancel();
      } else {
        this.props.onSave(modifiedFormFields);
      }
    }
  };

  _onCancel = () => {
    this.props.onCancel();
  };

  render() {
    const { updateError={}, requesting } = this.props;
    const { errors, firstName, lastName, email, role } = this.state;
    const { firstNameId, lastNameId, emailId } = elementIds;
    const firstNameErrorLabel = errors.missingFirstName ? missingValueLabel : '';
    const lastNameErrorLabel = errors.missingLastName ? missingValueLabel : '';
    const emailErrorLabel = errors.missingEmail ? missingValueLabel : '';
    const roleErrorLabel = errors.missingRole ? missingValueLabel : '';

    const errorMessage = Object.keys(updateError).map((errorKey) => (
      <Message role="alert" negative>
        <Message.Header>
          <FormattedMessage {...messages[errorKey]} />
        </Message.Header>
      </Message>
    ));

    return (
      <Dimmer.Dimmable as={Form} id="form-update-user" onSubmit={this._onSubmit} size="large">
        <Dimmer
          role="progressbar"
          aria-describedby="Updating User..."
          aria-busy={requesting}
          inverted
          active={requesting}
        >
          <Loader active={requesting} />
        </Dimmer>
        <Segment>
          <Form.Field styleName="change-profile-form-field">
            <label htmlFor={firstNameId}>
              <FormattedMessage {...messages.firstName} />
            </label>
            <Input type="text" name="firstName" value={firstName} onChange={this._onChange}>
              <input id={firstNameId} required aria-invalid={!!firstNameErrorLabel} />
            </Input>
            {firstNameErrorLabel}
          </Form.Field>
          <Form.Field styleName="change-profile-form-field">
            <label htmlFor={lastNameId}>
              <FormattedMessage {...messages.lastName} />
            </label>
            <Input type="text" name="lastName" value={lastName} onChange={this._onChange}>
              <input id={lastNameId} required aria-invalid={!!lastNameErrorLabel} />
            </Input>
            {lastNameErrorLabel}
          </Form.Field>
          <Form.Field styleName="change-profile-form-field">
            <label htmlFor={emailId}>
              <FormattedMessage {...messages.email} />
            </label>
            <Input type="email" name="email" value={email} onChange={this._onChange}>
              <input
                id={emailId}
                aria-required
                pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$"
                required
                aria-invalid={!!emailErrorLabel}
              />
            </Input>
            {emailErrorLabel}
          </Form.Field>
          <Form.Field>
            <fieldset
              aria-label={elementIds.roleId}
              id={elementIds.roleId}
              styleName="user-form-role"
              role="radiogroup"
              aria-required
              aria-invalid={!!roleErrorLabel}
            >
              <legend styleName="role-legend">
                <FormattedMessage {...messages.role} />
              </legend>
              {constants.APP_ROLES.map((roleConst) => (
                <div className="ui radio checkbox" styleName="roleRadio" key={`role_${roleConst}`}>
                  <input
                    id={`role_${roleConst}`}
                    type="radio"
                    name="role"
                    aria-label={roleConst}
                    value={roleConst}
                    styleName="role-checkbox"
                    checked={role === roleConst}
                    onChange={this._onChange}
                    required
                  />
                  <label htmlFor={`role_${roleConst}`} styleName="role-label">
                    <FormattedMessage {...repvisitMessages[roleConst]} />
                  </label>
                </div>
              ))}
            </fieldset>
            {roleErrorLabel}
          </Form.Field>
          {errorMessage}
          <Button type="button" onClick={this._onCancel}>
            <Icon name="remove" />
            <FormattedMessage {...messages.buttonCancel} />
          </Button>
          <Button primary type="submit">
            <Icon name="checkmark" />
            <FormattedMessage {...messages.buttonSave} />
          </Button>
        </Segment>
      </Dimmer.Dimmable>
    );
  }
}

export default UpdateUserForm;
